import {
  useState,
  useCallback,
  useRef,
  useEffect,
  lazy,
  Suspense,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useCVGenerationStore } from '../../stores/cvGenerationStore'
import type { CVDocument } from '../../stores/cvGenerationStore'
import { exportSuperDocDocx } from './SuperDocEditor'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import MobileCardEditor from './MobileCardEditor'
import MobilePreview from './MobilePreview'

const BASE_URL = import.meta.env.VITE_API_URL ?? ''

const SuperDocEditor = lazy(() => import('./SuperDocEditor'))

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve((reader.result as string).split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export default function ReviewStep() {
  const { session, loadState, error, assemblyProgress, assemble, approve, enterEditing, saveDraft, saveDraftHtml, revertToSnapshot, resumeAssemblyIfNeeded, abandonSession } =
    useCVGenerationStore()
  const navigate = useNavigate()
  const loading = loadState === 'loading'

  const handleStartOver = useCallback(async () => {
    await abandonSession()
    // Clear any ?session= resume param so the page lands on a fresh JD form
    // instead of re-fetching the now-abandoned session.
    navigate('/cv', { replace: true })
  }, [abandonSession, navigate])

  const isDesktop = useMediaQuery('(min-width: 1024px)')

  const [fitExpanded, setFitExpanded] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [editorKey, setEditorKey] = useState(0)
  const [previewDocx, setPreviewDocx] = useState<Blob | null>(null)
  const [docxLoading, setDocxLoading] = useState(false)
  const [approving, setApproving] = useState(false)
  const [superDocReady, setSuperDocReady] = useState(false)

  // Mobile-specific state
  const [mobileDoc, setMobileDoc] = useState<CVDocument | null>(null)
  const [mobilePreview, setMobilePreview] = useState(false)
  const [mobileTab, setMobileTab] = useState<'edit' | 'analysis'>('edit')
  const mobileAutoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cardEditorScrollRef = useRef<number>(0)

  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasEditedRef = useRef(false)
  // Separate from hasEditedRef, which also gates SuperDoc docx re-initialisation.
  // This one tracks whether POST /cv/edit has been sent for the current editing
  // round, so iteration_count counts rounds rather than keystrokes. Reset on
  // revert, which starts a fresh round from the assembled snapshot.
  const editingSignalledRef = useRef(false)
  const [docxReady, setDocxReady] = useState(false)

  // Fetch branded preview docx for desktop SuperDoc initialisation.
  // Guard on cv_document being present: ReviewStep also renders during cv.assembly
  // (while assembly is still running), so the first mount may fire before cv_document
  // is written to state. The boolean dep !!cv_document re-triggers the effect the
  // moment assembly completes and the session refresh lands.
  const cvDocPresent = !!session?.state?.cv_document
  useEffect(() => {
    if (!session?.session_id || !isDesktop || !cvDocPresent) return
    setDocxLoading(true)
    fetch(`${BASE_URL}/api/v1/cv/sessions/${session.session_id}/preview-docx`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        setPreviewDocx(blob)
        setDocxLoading(false)
      })
      .catch(() => setDocxLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.session_id, cvDocPresent, isDesktop])

  // Latch docxReady once previewDocx arrives and the user hasn't edited yet
  useEffect(() => {
    if (previewDocx && !hasEditedRef.current && !docxReady) {
      setDocxReady(true)
    }
  }, [previewDocx, docxReady])

  // Initialise mobileDoc from assembled cvDoc once
  useEffect(() => {
    const cvDoc = session?.state?.cv_document
    if (cvDoc && mobileDoc === null) {
      setMobileDoc(cvDoc)
    }
  }, [session?.state?.cv_document, mobileDoc])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
      if (mobileAutoSaveRef.current) clearTimeout(mobileAutoSaveRef.current)
    }
  }, [])

  // Resume assembly polling when the page loads while assembly is already running
  // (e.g. after a page refresh mid-assembly). Only fires when ready with no cv_document.
  const cvDocRef = session?.state?.cv_document
  useEffect(() => {
    if (loadState === 'ready' && !cvDocRef) {
      resumeAssemblyIfNeeded()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.session_id])

  const signalEditing = useCallback(() => {
    if (editingSignalledRef.current) return
    editingSignalledRef.current = true
    // Fire-and-forget: telemetry must never block or fail the edit itself.
    void enterEditing()
  }, [enterEditing])

  const handleEditorUpdate = useCallback(() => {
    signalEditing()
    hasEditedRef.current = true
    setSaveStatus('saving')
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(async () => {
      const { getSuperDocHtml } = await import('./SuperDocEditor')
      const html = getSuperDocHtml()
      if (!html) return
      try {
        await saveDraftHtml(html)
        setSaveStatus('saved')
      } catch {
        try {
          await saveDraftHtml(html)
          setSaveStatus('saved')
        } catch {
          setSaveStatus('error')
        }
      }
    }, 2000)
  }, [saveDraftHtml, signalEditing])

  const handleMobileChange = useCallback(
    (doc: CVDocument) => {
      signalEditing()
      setMobileDoc(doc)
      if (mobileAutoSaveRef.current) clearTimeout(mobileAutoSaveRef.current)
      mobileAutoSaveRef.current = setTimeout(() => {
        saveDraft(doc)
      }, 2000)
    },
    [saveDraft, signalEditing],
  )

  const handlePreview = useCallback(() => {
    const el = document.getElementById('mobile-card-editor-scroll')
    if (el) cardEditorScrollRef.current = el.scrollTop
    setMobilePreview(true)
  }, [])

  const handleBackFromPreview = useCallback(() => {
    setMobilePreview(false)
    requestAnimationFrame(() => {
      const el = document.getElementById('mobile-card-editor-scroll')
      if (el) el.scrollTop = cardEditorScrollRef.current
    })
  }, [])

  if (!session) return null

  const { state } = session
  const cvDoc = state.cv_document
  const cvHtml = state.cv_html
  const gapAnalysis = state.gap_analysis

  if (!cvDoc) {
    if (loadState === 'error') {
      const canRetry = !!state.gap_analysis
      if (canRetry) {
        return (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <p className="text-sm text-red-600">{error || 'Assembly failed.'}</p>
            <div className="flex items-center gap-3">
              <button
                onClick={assemble}
                className="px-5 py-2 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] transition-all shadow-sm"
              >
                Try again
              </button>
              <button
                onClick={handleStartOver}
                className="px-5 py-2 text-sm font-medium text-[#545f6e] rounded-lg border border-[#dac2b6] hover:bg-[#f5ede9] transition-colors"
              >
                Start over
              </button>
            </div>
          </div>
        )
      }
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-4 max-w-sm mx-auto text-center">
          <p className="text-sm text-[#545f6e]">
            We're sorry — the session data was lost during a server restart and we can't recover your answers. Please start a new CV.
          </p>
          <button
            onClick={handleStartOver}
            className="px-5 py-2 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] transition-all shadow-sm"
          >
            Start over
          </button>
        </div>
      )
    }
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-64 bg-[#dac2b6]/20 rounded-full h-1.5 overflow-hidden">
          <div
            className="bg-[#833c0b] h-1.5 rounded-full transition-all duration-700"
            style={{ width: assemblyProgress?.pct != null ? `${assemblyProgress.pct}%` : '10%' }}
          />
        </div>
        <p className="text-sm text-[#545f6e]">
          {assemblyProgress?.message ?? 'Assembling your CV...'}
        </p>
      </div>
    )
  }

  const handleApprove = async () => {
    if (approving || loading) return
    setApproving(true)
    try {
      if (!isDesktop) {
        // Mobile path: send CVDocument JSON to brand renderer
        if (mobileAutoSaveRef.current) {
          clearTimeout(mobileAutoSaveRef.current)
          mobileAutoSaveRef.current = null
        }
        await approve(mobileDoc ?? cvDoc)
        return
      }
      // Desktop path: export docx from SuperDoc while the editor instance is
      // still mounted. PDF is converted server-side from this docx at approval.
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
        autoSaveTimerRef.current = null
      }
      const docxBlob = await exportSuperDocDocx()
      if (docxBlob) {
        const docxB64 = await blobToBase64(docxBlob)
        await approve(undefined, undefined, docxB64)
      } else {
        await approve()
      }
    } finally {
      setApproving(false)
    }
  }

  const isApproveDisabled = loading || approving || docxLoading || (!superDocReady && !mobilePreview)

  // ── Mobile layout ────────────────────────────────────────────────────────────
  if (!isDesktop) {
    return (
      <div className="fixed inset-0 bg-[#f8f9ff] flex flex-col z-[60]">
        {/* Top app bar */}
        <header className="flex items-center justify-between px-5 py-4 bg-white border-b border-[#dac2b6] flex-shrink-0 h-16">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[#003d60]">
              edit_note
            </span>
            <h1 className="text-lg font-semibold text-[#003d60]">
              Proof of Work
            </h1>
          </div>
          <span className="material-symbols-outlined text-[#545f6e]">
            account_circle
          </span>
        </header>

        {/* Tab switcher */}
        <div className="flex border-b border-[#dac2b6] bg-white flex-shrink-0">
          {(['edit', 'analysis'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setMobileTab(tab)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                mobileTab === tab
                  ? 'text-[#833c0b] border-b-2 border-[#833c0b]'
                  : 'text-[#545f6e]'
              }`}
            >
              {tab === 'edit' ? 'Edit' : 'Job Analysis'}
            </button>
          ))}
        </div>

        {/* Editor / analysis area */}
        <div className="flex-1 overflow-hidden relative">
          {mobileTab === 'analysis' ? (
            <div className="h-full overflow-y-auto">
              <div className="px-5 py-4 space-y-5">
                {/* JD Fit */}
                {gapAnalysis && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-semibold text-[#545f6e] uppercase tracking-widest">
                        JD Fit
                      </span>
                    </div>
                    <div className="space-y-2">
                      {(gapAnalysis.competencies_required ?? []).map((comp) => (
                        <div key={comp.name} className="flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              comp.coverage === 'strong'
                                ? 'bg-green-500'
                                : comp.coverage === 'partial'
                                  ? 'bg-amber-500'
                                  : 'bg-red-400'
                            }`}
                          />
                          <span className="text-xs text-[#1a1c1a] flex-1">
                            {comp.name}
                          </span>
                          <span
                            className={`text-[10px] uppercase tracking-wider font-medium ${
                              comp.coverage === 'strong'
                                ? 'text-green-600'
                                : comp.coverage === 'partial'
                                  ? 'text-amber-600'
                                  : 'text-red-500'
                            }`}
                          >
                            {comp.coverage}
                          </span>
                        </div>
                      ))}
                    </div>
                    {(gapAnalysis.gap_list ?? []).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#dac2b6]/30">
                        <p className="text-[10px] font-semibold text-[#54433a] uppercase tracking-widest mb-2">
                          Gaps
                        </p>
                        {(gapAnalysis.gap_list ?? []).map((gap) => (
                          <div key={gap.competency} className="text-xs text-[#545f6e] mb-1">
                            <span className="text-red-500">•</span>{' '}
                            {gap.competency}
                            {gap.suggestion && (
                              <span className="text-[#877369]"> — {gap.suggestion}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Revert */}
                {saveStatus !== 'idle' && (
                  <button
                    type="button"
                    onClick={async () => {
                      const snapshot = await revertToSnapshot()
                      if (snapshot) {
                        editingSignalledRef.current = false
                        setSaveStatus('idle')
                        setMobileDoc(snapshot)
                        setMobileTab('edit')
                      }
                    }}
                    disabled={loading}
                    className="text-xs text-[#545f6e] hover:text-[#833c0b] transition-colors flex items-center gap-1 disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-base">undo</span>
                    Revert to generated CV
                  </button>
                )}
              </div>
            </div>
          ) : mobilePreview ? (
            <MobilePreview
              cvDoc={mobileDoc ?? cvDoc}
              onBack={handleBackFromPreview}
              onApprove={handleApprove}
              approving={approving || loading}
            />
          ) : (
            <MobileCardEditor
              cvDoc={mobileDoc ?? cvDoc}
              onChange={handleMobileChange}
              onPreview={handlePreview}
            />
          )}
        </div>
      </div>
    )
  }

  // ── Desktop layout (unchanged) ────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left panel — Analysis */}
        <div className="lg:col-span-4 space-y-4">
          {/* JD Fit Summary */}
          {gapAnalysis && (
            <div className="bg-white rounded-xl border border-[#dac2b6]/20 overflow-hidden">
              <button
                onClick={() => setFitExpanded(!fitExpanded)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#f4f3f0]/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-[#54433a] uppercase tracking-wider">
                    JD Fit Summary
                  </span>
                </div>
                <span className="material-symbols-outlined text-[#877369] text-lg">
                  {fitExpanded ? 'expand_less' : 'expand_more'}
                </span>
              </button>

              {fitExpanded && (
                <div className="px-5 pb-4 border-t border-[#dac2b6]/10 pt-3 space-y-3">
                  {(gapAnalysis.competencies_required ?? []).map((comp) => (
                    <div key={comp.name} className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          comp.coverage === 'strong'
                            ? 'bg-green-500'
                            : comp.coverage === 'partial'
                              ? 'bg-amber-500'
                              : 'bg-red-400'
                        }`}
                      />
                      <span className="text-xs text-[#1a1c1a] flex-1">
                        {comp.name}
                      </span>
                      <span
                        className={`text-[10px] uppercase tracking-wider font-medium ${
                          comp.coverage === 'strong'
                            ? 'text-green-600'
                            : comp.coverage === 'partial'
                              ? 'text-amber-600'
                              : 'text-red-500'
                        }`}
                      >
                        {comp.coverage}
                      </span>
                    </div>
                  ))}

                  {(gapAnalysis.gap_list ?? []).length > 0 && (
                    <div className="pt-2 border-t border-[#dac2b6]/10">
                      <p className="text-[10px] font-medium text-[#54433a] uppercase tracking-wider mb-2">
                        Gaps
                      </p>
                      {(gapAnalysis.gap_list ?? []).map((gap) => (
                        <div
                          key={gap.competency}
                          className="text-xs text-[#545f6e] mb-1"
                        >
                          <span className="text-red-500">•</span>{' '}
                          {gap.competency}
                          {gap.suggestion && (
                            <span className="text-[#877369]">
                              {' '}— {gap.suggestion}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Revert to generated CV */}
          {saveStatus !== 'idle' && (
            <button
              onClick={async () => {
                const snapshot = await revertToSnapshot()
                if (snapshot) {
                  editingSignalledRef.current = false
                  setSaveStatus('idle')
                  setSuperDocReady(false)
                  setEditorKey((k) => k + 1)
                }
              }}
              disabled={loading}
              className="text-xs text-[#545f6e] hover:text-[#833c0b] transition-colors flex items-center gap-1 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">undo</span>
              Revert to generated CV
            </button>
          )}
        </div>

        {/* Right panel — SuperDoc Editor */}
        <div className="lg:col-span-8">
          <div className="bg-white rounded-xl border border-[#dac2b6]/20 shadow-[0px_4px_20px_rgba(47,49,47,0.04)]">
            {/* Status bar */}
            <div className="px-5 py-3 border-b border-[#dac2b6]/10 flex items-center justify-between">
              <span className="text-xs text-[#877369]">
                {superDocReady ? 'Edit your CV directly below' : 'Loading document…'}
              </span>
              <div className="flex items-center gap-2">
                {saveStatus === 'saving' && (
                  <>
                    <span className="animate-spin inline-block w-2 h-2 border border-[#877369]/30 border-t-[#877369] rounded-full" />
                    <span className="text-xs text-[#877369]">Saving…</span>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-xs text-[#877369]">Saved</span>
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <span className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="text-xs text-red-500">
                      Failed to save — edits captured on Approve
                    </span>
                  </>
                )}
                {saveStatus === 'idle' && (
                  <>
                    <span className="w-2 h-2 rounded-full bg-stone-300" />
                    <span className="text-xs text-[#877369]">No changes</span>
                  </>
                )}
              </div>
            </div>

            {/* SuperDoc Editor */}
            <div className="pb-8 px-8 max-h-[70vh] overflow-y-auto">
              {docxLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="flex items-center gap-3">
                    <span className="animate-spin inline-block w-5 h-5 border-2 border-[#833c0b]/30 border-t-[#833c0b] rounded-full" />
                    <p className="text-sm text-[#545f6e]">
                      Preparing document…
                    </p>
                  </div>
                </div>
              ) : (
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center py-20">
                      <span className="animate-spin inline-block w-5 h-5 border-2 border-[#833c0b]/30 border-t-[#833c0b] rounded-full" />
                    </div>
                  }
                >
                  <SuperDocEditor
                    key={`${editorKey}-${docxReady ? 'docx' : 'html'}`}
                    document={cvDoc}
                    initialDocx={previewDocx ?? undefined}
                    initialHtml={cvHtml ?? undefined}
                    onUpdate={handleEditorUpdate}
                    onReady={() => setSuperDocReady(true)}
                  />
                </Suspense>
              )}
            </div>
          </div>

          {/* Action bar */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-[#877369]">
              Edit directly in the document above
            </p>
            <button
              onClick={handleApprove}
              disabled={isApproveDisabled}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
            >
              {loading || approving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  Approving...
                </span>
              ) : (
                'Approve & Export →'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
